import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Minus, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLang } from '../i18n';
import { eur, SHOPIFY_READY } from '../lib/shopify';
import { SmartImg } from './Image';
import { ChromeStar } from './ChromeStar';
import { EASE } from './motion';

export function CartDrawer() {
  const {
    isOpen,
    setOpen,
    lines,
    count,
    subtotal,
    updateQuantity,
    removeLine,
    addLine,
    checkout,
    busy,
  } = useCart();
  const { t } = useLang();

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            data-testid="cart-overlay"
            className="fixed inset-0 z-50 bg-black/30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-bone border-l border-black/10"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.55, ease: EASE }}
          >
            <div className="flex items-center justify-between border-b border-black/10 px-5 py-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.3em]">
                {t('cart.title')} — {count}
              </span>
              <button
                type="button"
                data-testid="cart-close-button"
                onClick={() => setOpen(false)}
                aria-label="Close bag"
                className="p-1 transition-transform duration-300 hover:rotate-90"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5">
              {lines.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <ChromeStar className="h-6 w-6 opacity-60" spin />
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                    {t('cart.empty')}
                  </p>
                  <button
                    type="button"
                    data-testid="cart-continue-button"
                    onClick={() => setOpen(false)}
                    className="link-underline font-mono text-[11px] uppercase tracking-[0.3em]"
                  >
                    {t('cart.continue')}
                  </button>
                </div>
              ) : (
                <ul className="divide-y divide-black/10">
                  {lines.map((line) => (
                    <li
                      key={line.key}
                      data-testid="cart-line-item"
                      className="flex gap-4 py-5"
                    >
                      <SmartImg
                        src={line.product.image}
                        alt={line.product.title}
                        label={line.product.title}
                        className="h-20 w-16 shrink-0 object-cover"
                      />
                      <div className="flex flex-1 flex-col justify-between">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-mono text-[11px] font-medium uppercase tracking-[0.2em]">
                              {line.product.title}
                            </p>
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                              {line.variant.title}
                              {line.variant.sku ? ` — ${line.variant.sku}` : ''}
                            </p>
                          </div>
                          <span className="font-mono text-[11px] tracking-[0.1em]">
                            {eur(Number(line.variant.price) * line.quantity)}
                          </span>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center border border-black/15">
                            <button
                              type="button"
                              data-testid="qty-decrease-button"
                              aria-label="Decrease quantity"
                              disabled={busy}
                              onClick={() => updateQuantity(line.key, line.quantity - 1)}
                              className="px-2.5 py-1.5 disabled:opacity-40"
                            >
                              <Minus className="h-3 w-3" strokeWidth={1.5} />
                            </button>
                            <span
                              data-testid="cart-line-quantity"
                              className="min-w-8 text-center font-mono text-[11px]"
                            >
                              {line.quantity}
                            </span>
                            <button
                              type="button"
                              data-testid="qty-increase-button"
                              aria-label="Increase quantity"
                              disabled={busy}
                              onClick={() => updateQuantity(line.key, line.quantity + 1)}
                              className="px-2.5 py-1.5 disabled:opacity-40"
                            >
                              <Plus className="h-3 w-3" strokeWidth={1.5} />
                            </button>
                          </div>
                          <button
                            type="button"
                            data-testid="cart-remove-button"
                            onClick={() => removeLine(line.key)}
                            className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 underline-offset-4 hover:underline"
                          >
                            {t('cart.remove')}
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

            </div>

            {lines.length > 0 && (
              <div className="border-t border-black/10 px-5 py-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.3em]">
                    {t('cart.subtotal')}
                  </span>
                  <span data-testid="cart-subtotal" className="font-mono text-sm">
                    {eur(subtotal)}
                  </span>
                </div>
                <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">
                  {t('cart.note')}
                </p>
                <button
                  type="button"
                  data-testid="checkout-button"
                  onClick={checkout}
                  className="mt-4 w-full rounded-full bg-ink py-4 font-mono text-[11px] uppercase tracking-[0.35em] text-bone transition-colors duration-300 hover:bg-neutral-700"
                >
                  {t('cart.checkout')}
                </button>
                <p className="mt-3 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500">
                  {t('cart.shippingNote')}
                </p>
                {!SHOPIFY_READY && (
                  <p
                    data-testid="checkout-mock-note"
                    className="mt-3 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-400"
                  >
                    {t('cart.mock')}
                  </p>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
